import type { Vec3 } from '../../geo/types';
import type { MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { SHIP_TEMPLATES } from './shipTemplates';
import {
  WATERLINE,
  formedTemplate,
  hullPoint,
  keelAt,
  railAt,
  type FormedTemplate,
} from './shipForm';
import { loftSolid, type Ring3 } from './shipSolids';
import { timberBox } from './shipTimber';

/*
 * A seagoing hull on the stocks in the Lastage yard, as the woodcut draws them (woodcut 880-1187 x
 * 3012-3097): no rig, no castles, its keel up on blocks and its sides propped by shores; planked
 * from the keel up to a share of its depth, its frames standing open above the planking to the
 * rail, a ribband along their heads, and a square stern. Built on the bark's lines, stretched to
 * the length and depth read at the woodcut's picture scale (docs/plans/ships.md 15).
 */
export const STOCKS = {
  /** The keel line's height over the ground: the woodcut's blocks, 7-12 px under the keels. */
  blockHeight: 0.9,
  /** The woodcut's frame heads, 7-8 px apart on its hulls. */
  frameSpacing: 1.0,
  frameSize: 0.2,
  plank: 0.08,
  keel: 0.3,
  ribband: 0.15,
  block: 0.4,
  shore: 0.18,
  /** How far beyond the widest point of the hull a shore's foot stands. */
  shoreReach: 0.8,
  blocksAt: [0.3, 0.45, 0.6, 0.75, 0.88],
  shoresAt: [0.35, 0.55, 0.75],
} as const;

export interface StocksHullSpec {
  readonly length: number;
  /** Keel to rail amidships. */
  readonly depth: number;
  /** The planking's top, as a share of the depth. */
  readonly planked: number;
}

const BASE = formedTemplate(SHIP_TEMPLATES.bark);
/* The section rows the frames bend through, keel to rail. */
const ROWS = [0, 0.25, 0.5, 0.65, 0.85, 1] as const;
/* A strake no thinner than this share of the depth. */
const MIN_STRAKE = 0.02;
const STRAKE_SAMPLES = 32;
/* The half-breadth low on the side where two layers of planking still leave the hold open. */
const SHELL_ROOM = 0.3;
const TANGENT_STEP = 0.01;
/* The gap kept between a frame's port and starboard arms where the hull narrows. */
const FRAME_CLEAR = 0.05;
const X: Vec3 = [1, 0, 0];
const Z: Vec3 = [0, 0, 1];

/** The bark's lines at the spec's length (and beam in proportion) and depth. */
export function stocksTemplate(spec: StocksHullSpec): FormedTemplate {
  const along = spec.length / BASE.length;
  const up = spec.depth / (railAt(BASE, 0.5) - keelAt(BASE, 0.5));
  const lifted = (height: number): number => up * (height + WATERLINE) - WATERLINE;
  const { form } = BASE;
  return {
    ...BASE,
    length: spec.length,
    beam: BASE.beam * along,
    masts: [],
    keelDepth: BASE.keelDepth * up,
    sheerFore: BASE.sheerFore * up,
    sheerAft: BASE.sheerAft * up,
    form: {
      ...form,
      waistRail: lifted(form.waistRail),
      stemRake: form.stemRake * along,
      transom: {
        ...form.transom,
        tuck: lifted(form.transom.tuck),
        rake: form.transom.rake * along,
      },
    },
  };
}

/** The ship frame's origin over the ground, so that the level keel rests on the blocks. */
export function stocksLift(template: FormedTemplate): number {
  return STOCKS.blockHeight - keelAt(template, 0.5);
}

/* A frame stands only where the hull is wide enough to take it inside both sides of the
   planking; nearer the posts the stem and the sternpost close the hull. */
function frameFits(template: FormedTemplate, s: number): boolean {
  const room = STOCKS.plank + STOCKS.frameSize + FRAME_CLEAR;
  return ROWS.slice(1).every((u) => hullPoint(template, s, u)[2] >= room);
}

/** Where the frames stand along the hull, 0 at the stem, a frame spacing apart. */
export function stocksFrameStations(spec: StocksHullSpec): readonly number[] {
  const template = stocksTemplate(spec);
  const n = Math.max(1, Math.round(spec.length / STOCKS.frameSpacing));
  return Array.from({ length: n }, (_, i) => (i + 0.5) / n).filter((s) => frameFits(template, s));
}

/** The row heights the strakes run between, from the keel to the planking's top. */
export function strakeRows(planked: number): readonly number[] {
  if (planked <= 0) return [];
  return [...ROWS.filter((u) => u < planked - MIN_STRAKE), planked];
}

/** The ground a hull and its shores stand on, along and across its keel. */
export function stocksFootprint(spec: StocksHullSpec): { length: number; width: number } {
  const beam = BASE.beam * (spec.length / BASE.length);
  return { length: spec.length + STOCKS.keel, width: beam + 2 * STOCKS.shoreReach + STOCKS.shore };
}

const add = (a: Vec3, b: Vec3, k = 1): Vec3 => [a[0] + k * b[0], a[1] + k * b[1], a[2] + k * b[2]];
const unit = (v: Vec3): Vec3 => {
  const n = Math.hypot(...v);
  return [v[0] / n, v[1] / n, v[2] / n];
};
const cross = (a: Vec3, b: Vec3): Vec3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];

/**
 * A square timber along a path: `width` along `side`, `depth` across both. The ring turns so that
 * the sweep crossed with its tangent points out of the timber (shipSolids.loftSolid).
 */
function pathTimber(
  builder: MeshBuilder,
  style: VertexStyle,
  path: readonly Vec3[],
  side: Vec3,
  size: readonly [number, number]
): void {
  const [w, d] = [size[0] / 2, size[1] / 2];
  const rings = path.map((p, i): Ring3 => {
    const a = path[Math.max(i - 1, 0)]!;
    const b = path[Math.min(i + 1, path.length - 1)]!;
    const across = unit(cross(unit(add(b, a, -1)), side));
    const at = (s: number, n: number): Vec3 => add(add(p, side, s * w), across, n * d);
    return [at(-1, -1), at(-1, 1), at(1, 1), at(1, -1)];
  });
  loftSolid(builder, style, rings);
}

/* The unit normal into the hull at a point of the starboard section, in its own plane. */
function inward(template: FormedTemplate, s: number, u: number): Vec3 {
  const [lo, hi] = [Math.max(0, u - TANGENT_STEP), Math.min(1, u + TANGENT_STEP)];
  const [, y0, z0] = hullPoint(template, s, lo);
  const [, y1, z1] = hullPoint(template, s, hi);
  const [dy, dz] = [y1 - y0, z1 - z0];
  const n = Math.hypot(dy, dz);
  return n > 0 ? [0, dz / n, -dy / n] : [0, 1, 0];
}

const mirror = ([x, y, z]: Vec3): Vec3 => [x, y, -z];

/* The starboard outside point lifted onto the stocks, and the point `inset` inside it; near the
   posts, where the hull is thinner than that, the point on the centre plane on the way in. */
function sidePoint(template: FormedTemplate, lift: number, s: number, u: number, inset = 0): Vec3 {
  const [x, y, z] = hullPoint(template, s, u);
  const [, ny, nz] = inward(template, s, u);
  const k = nz < 0 ? Math.min(inset, z / -nz) : inset;
  return [x, y + lift + k * ny, z + k * nz];
}

/* Where the hull is too narrow for two layers of planking with room between, it is built solid, as
   the posts and their deadwood are. */
function shellFits(template: FormedTemplate, s: number): boolean {
  return hullPoint(template, s, ROWS[1])[2] >= SHELL_ROOM;
}

/*
 * The planking's section at `s`, as the open boats' shell (openBoat.ts): the keel, the outside up
 * the starboard side to the planking's top, the inside down to the floor and its middle, up the
 * inside of the port side and down its outside. Where the hull is too narrow for a hold, the inside
 * points lie on the line across the planking's top instead, so the section is whole there; the
 * transom's is always whole, so the shell ends in a solid section, not in an open ring.
 */
function shellRing(
  template: FormedTemplate,
  lift: number,
  rows: readonly number[],
  s: number
): Ring3 {
  const [keel, ...outside] = rows.map((u) => sidePoint(template, lift, s, u));
  const top = outside.at(-1)!;
  const open = s > 0 && s < 1 && shellFits(template, s);
  const across = (k: number): Vec3 => [top[0], top[1], (top[2] * k) / (outside.length + 1)];
  const inside = open
    ? rows
        .slice(1)
        .map((u) => sidePoint(template, lift, s, u, STOCKS.plank))
        .reverse()
    : outside.map((_, i) => across(outside.length - i));
  const middle: Vec3 = open ? [inside.at(-1)![0], inside.at(-1)![1], 0] : across(0);
  const port = [...inside.map(mirror).reverse(), ...outside.map(mirror).reverse()];
  return [keel!, ...outside, ...inside, middle, ...port];
}

function buildShell(
  builder: MeshBuilder,
  style: VertexStyle,
  template: FormedTemplate,
  lift: number,
  planked: number
): void {
  const rows = strakeRows(planked);
  if (rows.length === 0) return;
  const ss = Array.from({ length: STRAKE_SAMPLES + 1 }, (_, i) => i / STRAKE_SAMPLES);
  loftSolid(
    builder,
    style,
    ss.map((s) => shellRing(template, lift, rows, s))
  );
}

/* Each frame from the port rail down round the keel and up to the starboard rail, inside the
   planking; the ribbands run along their heads. */
function buildFrames(
  builder: MeshBuilder,
  style: VertexStyle,
  template: FormedTemplate,
  lift: number,
  stations: readonly number[]
): void {
  const inset = STOCKS.plank + STOCKS.frameSize / 2;
  const size = [STOCKS.frameSize, STOCKS.frameSize] as const;
  for (const s of stations) {
    const starboard = ROWS.map((u) => sidePoint(template, lift, s, u, inset));
    const port = [...starboard].reverse().map(mirror);
    pathTimber(builder, style, [...port, ...starboard.slice(1)], X, size);
  }
  const heads = stations.map((s) =>
    sidePoint(template, lift, s, 1, STOCKS.plank + STOCKS.frameSize + STOCKS.ribband / 2)
  );
  const ribband = [STOCKS.ribband, STOCKS.ribband] as const;
  pathTimber(builder, style, heads, [0, 1, 0], ribband);
  pathTimber(builder, style, heads.map(mirror).reverse(), [0, 1, 0], ribband);
}

/* Keel, stem and sternpost on the centre line; the stem and post each from the keel to the rail. */
function buildSpine(
  builder: MeshBuilder,
  style: VertexStyle,
  template: FormedTemplate,
  lift: number
): void {
  const size = [STOCKS.keel, STOCKS.keel] as const;
  const centre = (s: number, u: number, down = 0): Vec3 => {
    const [x, y] = hullPoint(template, s, u);
    return [x, y + lift - down, 0];
  };
  const along = Array.from({ length: 17 }, (_, i) => centre(i / 16, 0, STOCKS.keel / 2));
  pathTimber(builder, style, along, Z, size);
  pathTimber(
    builder,
    style,
    ROWS.map((u) => centre(0, u)),
    Z,
    size
  );
  pathTimber(
    builder,
    style,
    ROWS.map((u) => centre(1, u)),
    Z,
    size
  );
}

/* Blocks under the level keel, and shores from the ground to the turn of the bilge each side. */
function buildStocks(
  builder: MeshBuilder,
  style: VertexStyle,
  template: FormedTemplate,
  lift: number
): void {
  const top = lift + keelAt(template, 0.5) - STOCKS.keel;
  const b = STOCKS.block / 2;
  for (const s of STOCKS.blocksAt) {
    const x = (s - 0.5) * template.length;
    timberBox(builder, style, [x - b, 0, -b], [x + b, top, b]);
  }
  const reach = template.beam / 2 + STOCKS.shoreReach;
  const size = [STOCKS.shore, STOCKS.shore] as const;
  for (const s of STOCKS.shoresAt) {
    const bilge = sidePoint(template, lift, s, 0.5);
    const foot: Vec3 = [bilge[0], STOCKS.shore / 2, reach];
    pathTimber(builder, style, [foot, bilge], X, size);
    pathTimber(builder, style, [mirror(bilge), mirror(foot)], X, size);
  }
}

/** A hull on the stocks in its own frame: bow at -X, the ground at y = 0. */
export function buildStocksHull(
  builder: MeshBuilder,
  spec: StocksHullSpec,
  style: VertexStyle
): void {
  const template = stocksTemplate(spec);
  const lift = stocksLift(template);
  buildSpine(builder, style, template, lift);
  buildShell(builder, style, template, lift, spec.planked);
  buildFrames(builder, style, template, lift, stocksFrameStations(spec));
  buildStocks(builder, style, template, lift);
}
